import { Component, Inject, OnInit } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Store } from '@ngxs/store';
import { AccountService } from 'app/core';
import { Role, User } from 'app/core/models';
import { AccountState } from 'app/state/account/state';
import { EMAIL_PATTERN } from 'app/shared/Utils/common.utils';
import { RoleService } from '../../roles-permissions/services/roles.service';
import * as CustomValidator from 'app/shared/directives/custom-validators.directive';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TeamsService } from '../../teams/services/teams.service';
import { shareReplay } from 'rxjs';
import { TsSnackbarService } from 'tsui';

@Component({
  selector: 'invite-user',
  templateUrl: './invite-user.component.html',
  styleUrls: ['./invite-user.component.scss'],
})
export class InviteUserComponent extends OnDestroyMixin implements OnInit {
  private accountId: number;
  private role: string = 'admin';
  private extra: any = {};
  private users: User[];

  public roles: Role[];
  public forceRoleName: string = undefined;
  public inviteUserForm: UntypedFormGroup;
  public isLoading: boolean;
  public readonly teamOptions$ = this.teamsService.getAllTeams().pipe(shareReplay());

  constructor(
    @Inject(DIALOG_DATA) public data: any,
    public dialogRef: DialogRef<InviteUserComponent>,
    private readonly accountService: AccountService,
    private readonly fb: UntypedFormBuilder,
    private readonly snackBar: TsSnackbarService,
    private readonly roleService: RoleService,
    private readonly teamsService: TeamsService,
    private readonly store: Store,
  ) {
    super();
    this.accountId = +this.store.selectSnapshot(AccountState.getAccount).id;
  }

  public get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.inviteUserForm.pristine || this.inviteUserForm.invalid;
  }

  ngOnInit(): void {
    this.accountService.users$.pipe(untilComponentDestroyed(this)).subscribe((users) => (this.users = users));

    if (this.data?.role) this.role = this.data.role;
    if (this.data?.extra) this.extra = this.data.extra;
    if (this.data?.forceRoleName) this.forceRoleName = this.data.forceRoleName;

    this.buildForm();

    this.roleService
      .getByAccountId(this.accountId)
      .pipe(untilComponentDestroyed(this))
      .subscribe((roles) => {
        this.roles = roles;
        if (this.forceRoleName) {
          const role = this.roles.find((role) => role.name === this.forceRoleName);
          this.inviteUserForm.get('role_id').setValue(role?.id);
        }
      });
  }

  private buildForm(): void {
    this.inviteUserForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(EMAIL_PATTERN),
          CustomValidator.uniqueNameValidator(this.users, 'email'),
        ],
      ],
      role_id: ['', [Validators.required]],
      team_id: ['', [Validators.required]],
    });
  }

  public saveAndClose(): void {
    this.inviteUserForm.disable();

    let invitation = this.inviteUserForm.value;
    invitation.extra = this.extra;
    this.accountService
      .sendInvitation(invitation)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.snackBar.open('Invitation has been sent.', 'success');
        this.discard();
      });
  }

  public discard(): void {
    this.dialogRef.close();
  }

  // Function attached to debounce-click directive to flag the button's disable state.
  public isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }
}
